<template>
  <div class="bg-white min-h-screen">
    <main class="flex flex-col mt-10">
      <div v-if="workingOn">
        <h1 class="w-full mb-5 bg-yellow-300 font-bold text-yellow-700 flex align-middle py-2 pl-2">
          {{ message }}
        </h1>
      </div>
      <section class="flex flex-col justify-between items-center md:flex-row lg:flex-row md:items-start lg:items-start">
        <div class="flex flex-col md:w-2/3 w-5/6 mx-auto md:pr-5">

          <checkout-addresses v-model="selectedShippingAddress" :shipping-to="shippingTo" />

          <section class="my-10" :aria-label="$t('checkout.shipping-to')">
            <h1 class="text-sm">
              {{ $t('checkout.shipping-to') }}
            </h1>
            <div class="flex flex-col">
              <div class="flex items-center">
                <input type="radio" v-model="shippingTo" value="billing" id="billing" role="checkbox">
                <label for="billing" class="pl-2 text-xs" :class="fontThin()">
                  {{ $t('checkout.billing-address') }}
                </label>
              </div>
              <div class="flex items-center">
                <input type="radio" v-model="shippingTo" value="shipping" id="shipping" role="checkbox">
                <label for="shipping" class="pl-2 text-xs" :class="fontThin()">
                  {{ $t('checkout.shipping-address') }}
                </label>
              </div>
            </div>
          </section>
          <section class="w-full flex flex-col justify-between mb-3 mt-10" :aria-label="$t('checkout.payment-method')">
            <div class="w-full my-10 md:my-0">
              <cart-payment-methods v-model="paymentMethod" :showCash="true" :shippingAddress="selectedShippingAddress" />
            </div>
            <checkout-payments :paymentMethod="paymentMethod ? paymentMethod.slug : ''" v-model="stripePaymentMethod" />
          </section>
        </div>
        <div class="w-4/5 md:w-1/3 lg:w-1/3 flex flex-col">
          <h1 class="border-b border-blackish">
            {{ $t('checkout.summary') }}
          </h1>
          <div class="py-3">
            <cart-products-list />
          </div>
          <div class="border-t border-blackish mt-3 py-2">
            <cart-totals :shippingMessage="shippingMessage" :paymentMethod="paymentMethod" v-model="couponCode" />
          </div>
          <checkout-button
            v-if="showCheckoutButton"
            v-model="workingOn"
            @updatedMessage="message = $event"
            :message="message"
            :selectedShippingAddress="selectedShippingAddress"
            :paymentMethod="paymentMethod"
            :stripePaymentMethod="stripePaymentMethod"
            :createOrder="true"
            :couponCode="couponCode" />
        </div>
      </section>
    </main>
  </div>
</template>

<script>

import axios from 'axios';

import CartPaymentMethods from '@/components/cart/CartPaymentMethods.vue';
import CartProductsList from '@/components/cart/CartProductsList.vue';
import CartTotals from '@/components/cart/CartTotals.vue';
import CheckoutAddresses from '@/components/CheckoutAddresses.vue';
import { mapGetters } from 'vuex';
import CheckoutPayments from '../components/CheckoutPayments.vue';
import CheckoutButton from '../components/CheckoutButton.vue';
import CheckoutMixin from '../mixins/CheckoutMixin';

export default {
  metaInfo() {
    const title = this.$t('titles.checkout');
    const description = this.$t('metaDescriptions.checkout');
    return {
      title,
      meta: [
        {
          name: 'description',
          content: description,
        },
      ],
    };
  },

  components: {
    CartPaymentMethods,
    CartProductsList,
    CartTotals,
    CheckoutAddresses,
    CheckoutPayments,
    CheckoutButton,
  },

  data() {
    return {
      shippingTo: 'billing',
      paymentMethod: this.$store.getters['cart/paymentMethod'] ? this.$store.getters['cart/paymentMethod'] : 'stripe',
      workingOn: false,
      message: '',

      customerType: 'F',

      selectedShippingAddress: null,

      stripePaymentMethod: 'last',

      isLoading: false,

      roundedTotal: null,

      shippingMethods: [],
      shippingMessage: '',

      couponCode: '',
    };
  },

  computed: {
    ...mapGetters({
      authenticated: ['auth/authenticated'],
      customer: ['auth/customer'],
      user: ['auth/user'],
      billingAddress: ['auth/billingAddress'],
      shippingAddresses: ['auth/shippingAddresses'],
      cartProducts: ['cart/products'],
      deletedPaymentMethod: ['cart/deletedPaymentMethod'],
      totalWithTaxes: ['cart/totalWithTaxes'],
      changedLocale: ['auth/changedLocale'],
      products: ['cart/products'],
      totalToPay: ['cart/totalToPay'],
    }),

    showCheckoutButton() {
      if (this.totalToPay <= 0) {
        return true;
      }
      if (!this.paymentMethod || !this.paymentMethod.slug) {
        return false;
      }
      if ((this.paymentMethod.slug === 'stripe' || this.paymentMethod.slug === 'sepa')
        && (!this.stripePaymentMethod || this.stripePaymentMethod === 'last')) {
        return false;
      }
      return true;
    },
  },

  watch: {
    customerType() {
      this.updateCustomerType();
    },

    selectedShippingAddress() {
      this.setShippingMessageAndPrice();
    },

    changedLocale() {
      this.setShippingMessageAndPrice();
    },

    products() {
      this.setShippingMessageAndPrice();
    },
  },

  created() {
    this.getShippingMethods();
  },

  mounted() {
    if (this.billingAddress) this.hasBillingAddress = true;
  },

  mixins: [CheckoutMixin],

  methods: {
    getShippingMethods() {
      return axios.get('/api/shipping-methods').then(({ data }) => {
        this.shippingMethods = [];
        this.shippingMethods = data;
        this.setShippingMessageAndPrice();
      }).catch((error) => {
        this.checkRoutingAuthentication(error);
      });
    },

    setShippingMessageAndPrice() {
      this.shippingMessage = this.setShippingMethod(this.shippingMethods,
        this.selectedShippingAddress ? this.selectedShippingAddress.country : null);
    },

    updateCustomerType() {
      const editedCustomer = { type: this.customerType };
      axios.put(`/api/customers/${this.customer.id}`, editedCustomer).then(() => {
        this.$store.dispatch('auth/auth');
      }).catch((error) => {
        this.checkSessionErrorNotification(error);
      });
    },

    hasAddedBillingAddress(address) {
      this.hasBillingAddress = true;
      this.selectedAddress = address;
      this.shippingTo = 'billing';
    },
  },
};
</script>

<style lang="scss" scoped>

::v-deep h1 {
  @apply uppercase font-bold text-xs tracking-wide w-full pb-2;
}

::v-deep ul {
  li:nth-child(n+2) {
    @apply  border-t border-gray-400;
  }
}

</style>
